import * as React from "react";

/**
 * Derivative entry from the build-time _derivatives.json manifest.
 * Each source image maps to multiple format/width variants + a tiny LQIP.
 */
interface DerivativeVariant {
  width: number;
  format: string;
  path: string;
  size: number;
}

interface DerivativeEntry {
  original: string;
  variants: DerivativeVariant[];
  lqip: string;
}

export type DerivativesManifest = Record<string, DerivativeEntry>;

let manifestPromise: Promise<DerivativesManifest> | null = null;

/**
 * Seed the manifest synchronously when it's been inlined into the page, so the
 * very first render (and the prerendered HTML) already carries the AVIF srcset
 * and LQIP — no runtime-fetch race that the <img> always wins:
 *  - Client (dev + prod): `window.__SA_DERIVATIVES__`, injected into the <head>
 *    (dev: the game-assets vite plugin; prod: scripts/prerender.ts).
 *  - SSR/prerender: `provideDerivativesManifest()`, called by entry-server
 *    before `renderToString` so the static HTML ships srcset + blur placeholder.
 * Falls back to `loadManifest()`'s fetch when neither seam ran.
 */
let cachedManifest: DerivativesManifest | null =
  typeof window !== "undefined" && window.__SA_DERIVATIVES__
    ? (window.__SA_DERIVATIVES__ as DerivativesManifest)
    : null;

/**
 * SSR seam: prerender provides the build manifest before rendering.
 *
 * Lifetime note: `cachedManifest` is a module-level singleton. This is
 * intentional for our two consumers — the build-time prerender (a one-shot Node
 * process where every render uses the same manifest) and the browser (seeded
 * once from `window.__SA_DERIVATIVES__`). It is NOT designed for a long-running
 * SSR server that swaps manifests between deploys: a later call would overwrite
 * the singleton for all subsequent renders, and a stale value would persist
 * until the process restarts. If we ever add a persistent SSR server, thread the
 * manifest through render context instead of this module global.
 */
export function provideDerivativesManifest(manifest: DerivativesManifest): void {
  cachedManifest = manifest;
}

/**
 * Load the derivatives manifest once. Returns the cached result on
 * subsequent calls. The manifest is small (~5 KB for 24 images) so
 * keeping it in memory is fine.
 */
function loadManifest(): Promise<DerivativesManifest> {
  if (cachedManifest) return Promise.resolve(cachedManifest);
  if (!manifestPromise) {
    manifestPromise = fetch("/_derivatives.json")
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load derivatives manifest: ${r.status}`);
        return r.json() as Promise<DerivativesManifest>;
      })
      .then((m) => {
        cachedManifest = m;
        return m;
      })
      .catch(() => {
        cachedManifest = {};
        return {} as DerivativesManifest;
      });
  }
  return manifestPromise;
}

export interface ResponsiveImageData {
  /** AVIF srcset string for <source type="image/avif"> */
  avifSrcset: string | null;
  /** WebP srcset string for <source type="image/webp"> */
  webpSrcset: string | null;
  /** Fallback src (original image path) */
  fallbackSrc: string;
  /** Base64 LQIP data URI for blur-up placeholder */
  lqip: string | null;
  /** Whether the manifest has loaded */
  isReady: boolean;
}

/**
 * Resolves a thumbnail/cover URL into responsive srcset data by looking up
 * the build-time derivatives manifest. The manifest maps base paths
 * (e.g. "/game-assets/blaster-gate/thumbnail") to AVIF/WebP variants at
 * multiple widths plus a tiny LQIP.
 *
 * Returns immediately with the fallback src + null srcsets, then updates
 * once the manifest loads. The component receives the reactive update and
 * can render progressive enhancement: LQIP blur → WebP/AVIF swap.
 */
export function useResponsiveImage(src: string | null | undefined): ResponsiveImageData {
  const [manifest, setManifest] = React.useState<DerivativesManifest | null>(cachedManifest);

  React.useEffect(() => {
    if (cachedManifest) {
      setManifest(cachedManifest);
      return;
    }
    let cancelled = false;
    void loadManifest().then((m) => {
      if (!cancelled) setManifest(m);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return React.useMemo(() => {
    if (!src) {
      return { avifSrcset: null, webpSrcset: null, fallbackSrc: "", lqip: null, isReady: true };
    }

    if (!manifest) {
      return { avifSrcset: null, webpSrcset: null, fallbackSrc: src, lqip: null, isReady: false };
    }

    const ext = src.match(/\.[a-z]+$/i)?.[0] ?? "";
    const baseKey = ext ? src.slice(0, -ext.length) : src;
    const entry = manifest[baseKey];

    if (!entry) {
      return { avifSrcset: null, webpSrcset: null, fallbackSrc: src, lqip: null, isReady: true };
    }

    const avifVariants = entry.variants
      .filter((v) => v.format === "avif")
      .map((v) => `${v.path} ${v.width}w`)
      .join(", ");

    const webpVariants = entry.variants
      .filter((v) => v.format === "webp")
      .map((v) => `${v.path} ${v.width}w`)
      .join(", ");

    return {
      avifSrcset: avifVariants || null,
      webpSrcset: webpVariants || null,
      fallbackSrc: src,
      lqip: entry.lqip || null,
      isReady: true,
    };
  }, [src, manifest]);
}
